import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { MobileFrameComponent } from '../../../preview-common/mobile-frame/mobile-frame.component';
import { YoutubeMobileBottomNavComponent } from '../../../preview-common/youtube/youtube-mobile-bottom-nav/youtube-mobile-bottom-nav.component';

/**
 * Layout for the YouTube Shorts ad — the vertical player with the advertiser
 * row, description and CTA painted over the bottom of the creative.
 *
 * Presentational only, and not registered as a variant itself: the registered
 * per-format components wrap it and pass their own {@link format}.
 */
@Component({
    selector: 'app-gen-youtube-shorts-ad-mobile-shell',
    imports: [CommonModule, MobileFrameComponent, YoutubeMobileBottomNavComponent],
    templateUrl: './youtube-shorts-ad-mobile-shell.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        '../../../preview-common/youtube/youtube-mobile-styles.scss',
        './youtube-shorts-ad-mobile-shell.component.scss',
    ]
})
export class YoutubeShortsAdMobileShellComponent {
  @Input() data!: GenVideoPreviewData;

  /**
   * The creative format this placement previews, hardcoded by the wrapping
   * variant. The matching video is found by measured format rather than by
   * slot, so the author can paste the links in any order.
   */
  @Input() format: VideoFormat = '9:16';

  /** 9:16 is the native Shorts frame; anything else needs the blurred bands. */
  get isFullscreen(): boolean {
    return this.format === '9:16';
  }

  /** Thumbnail of the video in this format, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailForFormat(this.videoIds, this.format);
  }

  /**
   * True while any link is still being measured. The empty slot says so instead
   * of asking for a video, so the first paint does not read as a missing asset
   * for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolvingAny(this.videoIds);
  }

  private get videoIds(): string[] {
    return this.data.videos.map((video) => video.videoId);
  }
}
